import { useCallback, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import {
  FICHA_SECTIONS,
  fieldsForSection,
  isFieldVisible,
  type FichaCata,
  type SeleccionDescriptores,
} from '@escuela/ficha-domain'
import { FieldRow } from './field-row'
import { SectionIcon } from './section-icon'

function isEmptyDescriptores(sel: SeleccionDescriptores | undefined): boolean {
  if (!sel) return true
  for (const grupo of Object.values(sel)) {
    for (const fam of Object.values(grupo)) {
      if (fam.seleccionados.length > 0 || fam.otros) return false
    }
  }
  return true
}

// Copia profunda para que los sabores no compartan referencias con los aromas de
// Nariz (editar Gusto no debe tocar Nariz). Paridad con el form de la app.
function cloneDescriptores(sel: SeleccionDescriptores): SeleccionDescriptores {
  const out: SeleccionDescriptores = {}
  for (const [g, grupo] of Object.entries(sel)) {
    out[g] = {}
    for (const [f, fam] of Object.entries(grupo)) {
      out[g][f] = { seleccionados: [...fam.seleccionados], otros: fam.otros }
    }
  }
  return out
}

interface FichaFormProps {
  value: FichaCata
  onChange: (ficha: FichaCata) => void
}

/**
 * Formulario compartido (mirror web del form de la app). Renderiza las 5
 * secciones a partir del field registry (`FICHA_FIELDS`); el comportamiento
 * (valores, opciones, condicionales) lo dicta el registry. Layout desktop:
 * nav lateral sticky de secciones + columna central. Todas las secciones se
 * renderizan en el DOM (los tests de paridad recorren `[data-field-path]`).
 */
export function FichaForm({ value, onChange }: FichaFormProps): ReactNode {
  const [active, setActive] = useState<string>(FICHA_SECTIONS[0]?.key ?? 'infoBasica')

  // Espejo Nariz->Gusto: los aromas de Nariz se replican en los sabores de Gusto
  // mientras el usuario no haya editado Gusto. Arranca activo solo si Gusto esta
  // vacio (al editar una ficha con sabores cargados no se pisa nada). Paridad con
  // el form de la app. Cada FieldRow muta un solo campo, asi que en cada onChange
  // cambia `nariz.aromas` XOR `gusto.sabores`.
  const [mirrorSabores, setMirrorSabores] = useState(() =>
    isEmptyDescriptores(value.gusto.sabores),
  )

  const handleChange = useCallback(
    (next: FichaCata) => {
      if (mirrorSabores && next.nariz.aromas !== value.nariz.aromas) {
        onChange({
          ...next,
          gusto: { ...next.gusto, sabores: cloneDescriptores(next.nariz.aromas) },
        })
        return
      }
      // Cualquier edicion del usuario sobre los sabores corta el espejo.
      if (mirrorSabores && next.gusto.sabores !== value.gusto.sabores) {
        setMirrorSabores(false)
      }
      onChange(next)
    },
    [mirrorSabores, value, onChange],
  )

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[200px_minmax(0,1fr)]">
      <nav className="hidden lg:block">
        <ul className="sticky top-6 flex flex-col gap-1">
          {FICHA_SECTIONS.map((section) => (
            <li key={section.key}>
              <a
                href={`#seccion-${section.key}`}
                onClick={() => setActive(section.key)}
                className={cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  active === section.key
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                <SectionIcon icon={section.icon} className="h-4 w-4" />
                {section.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="flex flex-col gap-10">
        {FICHA_SECTIONS.map((section) => {
          const fields = fieldsForSection(section.key)
            .filter((f) => isFieldVisible(f, value))
            // condicionFruta se renderiza anidado dentro del cuadro Frutal del
            // descriptor (nariz.aromas / gusto.sabores), no como fila aparte.
            .filter((f) => !f.path.endsWith('.condicionFruta'))
          return (
            <section
              key={section.key}
              id={`seccion-${section.key}`}
              className="flex flex-col gap-5 scroll-mt-6"
            >
              <h2 className="flex items-center gap-2 border-b pb-2 text-lg font-semibold tracking-tight">
                <SectionIcon icon={section.icon} className="h-5 w-5" />
                {section.label}
              </h2>
              {fields.map((field) => (
                <FieldRow
                  key={field.path}
                  field={field}
                  ficha={value}
                  onChange={handleChange}
                />
              ))}
            </section>
          )
        })}
      </div>
    </div>
  )
}
